import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import SelectReportTemplate from '@/components/features/reports/templates/select';
import FillTask from "@/components/features/tasks/fill";
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTRPC } from "@/trpc"
import { useAuth } from "@/hooks/use-auth";
import React from "react"
import { hasRight } from '@/config/rights.config'
import * as Icons from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES_CONFIG } from '@/config/routes.config';

interface Player {
  id: string;
  name: string;
  position: string;
  shirtNumber: number;
  clubId?: string;
  playerId: string;
}

interface Competition {
  id: string;
  name: string;
}

interface Props {
  players: Player[];
  competition: Competition;
}

const PlayersTable = ({ players, competition }: Props) => {
  const { trpc } = useTRPC();
  const { user } = useAuth();

  const navigate = useNavigate();

  const [selectedPlayer, setSelectedPlayer] = React.useState<Player | null>(null);
  const [createdTask, setCreatedTask] = React.useState<string>("");
  const [fillTaskOpen, setFillTaskOpen] = React.useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = React.useState(false);

  const { data: templates } = useQuery({
    queryKey: ["report-templates", "screening"],
    queryFn: async () => {
      return await trpc.reports.templates.search.query({ type: ["screening"] });
    },
    staleTime: 1000 * 60, // 1 min cache
  });

  const { mutate: createTask } = useMutation({
    mutationFn: async (data: {
      assigned_user_id: string;
      report_id: string;
      club_id?: string;
      mongo_player_id?: string;
      ext_club_id?: string;
      competition_id?: string;
    }) => trpc.tasks.create.mutate(data),
    onSuccess: (task) => {
      setCreatedTask(task.id);
      setFillTaskOpen(true);
      setSelectedPlayer(null);
      setTemplateDialogOpen(false);
    },
    onError: (err) => {
      if (err instanceof Error) toast.error(err.message);
      else toast.error("Er is een onbekende fout opgetreden.");
    }
  });

  const handleReportTemplateSelect = (templateId: string | undefined) => {
    if (!templateId || !selectedPlayer || !user?.id) return;
    createTask({
      assigned_user_id: user.id,
      report_id: templateId,
      mongo_player_id: selectedPlayer.playerId,
      ext_club_id: selectedPlayer.clubId,
      competition_id: competition.id,
    });
  };

  const handleRowClick = (player: Player) => {
    if (templates?.reports && templates.reports.length === 1 && user?.id) {
      const templateId = templates.reports[0].id;
      createTask({
        assigned_user_id: user.id,
        report_id: templateId,
        mongo_player_id: player.playerId,
        ext_club_id: player.clubId,
        competition_id: competition.id,
      });
      return;
    }

    setSelectedPlayer(player);
    setTemplateDialogOpen(true);
  };

  return (
    <div className="overflow-x-auto">
      <Table className="min-w-[420px]">
        <colgroup>
          <col style={{ width: "40%" }} />
          <col style={{ width: "30%" }} />
          <col style={{ width: "30%" }} />
        </colgroup>
        <TableHeader>
          <TableRow>
            <TableHead>Nr.</TableHead>
            <TableHead>Naam</TableHead>
            <TableHead>Positie</TableHead>
            <TableHead>Spelers profiel</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {hasRight(user?.role?.name, "TASKS.WRITE") &&
            players.map((player: Player) => (
              <TableRow
                key={player.id}
                className="cursor-pointer hover:bg-foreground/10"
                onClick={() => handleRowClick(player)}
              >
                <TableCell>{player.shirtNumber}</TableCell>
                <TableCell>{player.name}</TableCell>
                <TableCell>{player.position}</TableCell>
                <TableCell className="flex align-center justify-center" 
                  onClick={() => 
                    { if(player?.id === undefined) return; 
                  navigate(ROUTES_CONFIG.APP.PLAYER_DETAIL.$buildPath({ params: { player_id: player?.playerId } }))}} >
                  <Icons.EyeIcon className="w-4 h-4" /></TableCell>
              </TableRow>
            ))
          }
        </TableBody>
      </Table>

      <div className="hidden">
        <SelectReportTemplate
          open={templateDialogOpen}
          onValueChange={handleReportTemplateSelect}
          types={["screening"]}
          loadAllIfNotypesFound={true}
          onOpenChange={(open) => {
            setTemplateDialogOpen(open);
            if (!open) setSelectedPlayer(null);
          }}
        />
      </div>

      {fillTaskOpen ? (
        <FillTask
          task_id={createdTask}
          open={fillTaskOpen}
          onOpenChange={setFillTaskOpen}
        />
      ) : null}
    </div>
  );
};

export default PlayersTable;